import { useCallback } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { useLocation } from 'wouter';
import { triggerWonCelebration } from '@/utils/confetti';

// Pipeline compound components from feature folder
import {
  Pipeline,
  usePipelineContext,
  GlobalSearchResultsPanel,
  PipelineDetailSheet,
} from '@/features/pipeline';

// Page-level components (NOT in feature folder - stay at page level)
import {
  SimplePipelineFilters,
} from '@/components/pipeline/SimplePipelineFilters';
import { CalendarPipelineView } from '@/components/pipeline/CalendarPipelineView';
import { WonStageModal } from '@/components/pipeline/WonStageModal';
import { InstallationCompletedModal } from '@/components/pipeline/InstallationCompletedModal';
import { PipelineBookingPopup } from '@/components/pipeline/PipelineBookingPopup';

/**
 * Pipeline Page - Main sales pipeline view
 *
 * This page wraps the Pipeline compound components with the PipelineProvider
 * and handles page-level modals (Won, Installation Completed, Booking).
 *
 * The compound components handle:
 * - Pipeline.Header: Search, view mode, layout toggles, add lead
 * - Pipeline.Overview: Stats bar with progress
 * - Pipeline.Board: Stage rendering with card drag-drop
 * - Pipeline.TrashZone: Remove cards from pipeline
 * - GlobalSearchResultsPanel: Search results with virtualization
 *
 * Page-level components:
 * - SimplePipelineFilters: Filter dropdown
 * - CalendarPipelineView: Calendar mode
 * - WonStageModal: Confirm move to Won stage (triggers backend automations)
 * - InstallationCompletedModal: Confirm move to Installation Completed stage
 * - PipelineBookingPopup: Book appointments
 */
export default function PipelinePage() {
  return (
    <TooltipProvider>
      <Pipeline.Provider>
        <PipelinePageContent />
      </Pipeline.Provider>
    </TooltipProvider>
  );
}

/**
 * Pipeline page content - uses context from PipelineProvider
 */
function PipelinePageContent() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { state, actions, meta } = usePipelineContext();

  // Destructure state for easier access
  const {
    stages,
    customers,
    isLoading,
    error,
    viewMode,
    filters,
    wonModalOpen,
    installationModalOpen,
    pendingStageChange,
    bookingPopupOpen,
    bookingCustomer,
    focusMatchesOnly,
    detailSheetOpen,
    selectedCustomerId,
  } = state;

  // Destructure meta for easier access
  const {
    filteredCustomers,
    highlightTerms,
    totalMatches,
    globalSearchResults,
    stageSummaries,
    stageById,
  } = meta;

  // Get selected customer for detail sheet
  const selectedCustomer = selectedCustomerId
    ? customers.find((c) => c.id === selectedCustomerId) ?? null
    : null;

  // Loading state
  if (isLoading) {
    return <Pipeline.LoadingSkeleton />;
  }

  // Error state
  if (error) {
    return <PipelineErrorState />;
  }

  // Won modal handler
  const handleWonModalConfirm = async (preferences: {
    sendSolarJourney: boolean;
    generateLOA: boolean;
    triggerOnboarding: boolean;
    createInstallation: boolean;
    dealValue?: number;
  }) => {
    if (!pendingStageChange) return;

    try {
      // First, update the deal value if provided
      if (preferences.dealValue && preferences.dealValue > 0) {
        await apiRequest('PATCH', `/api/customers/${pendingStageChange.customerId}`, {
          dealValue: Math.round(preferences.dealValue * 100), // Convert to cents
        });
      }

      // Then move the customer to Won stage with automation preferences
      await apiRequest('POST', '/api/pipeline/history/move', {
        customerId: pendingStageChange.customerId,
        fromStageId: pendingStageChange.customer.pipelineStageId,
        toStageId: pendingStageChange.toStageId,
        automationPreferences: preferences,
      });

      toast({
        title: 'Deal Won!',
        description: `${pendingStageChange.customer.name} has been moved to Won stage`,
      });

      // Trigger confetti celebration for the win!
      triggerWonCelebration();

      // Refresh pipeline data
      queryClient.invalidateQueries({ queryKey: ['/api/pipeline-stages/data'] });
    } catch (err) {
      const error = err as Error;
      toast({
        title: 'Error',
        description: `Failed to move customer: ${error.message}`,
        variant: 'destructive',
      });
    } finally {
      actions.closeModals();
    }
  };

  // Installation completed modal handler
  const handleInstallationModalConfirm = async (preferences: {
    sendInstallationEmail: boolean;
  }) => {
    if (!pendingStageChange) return;

    try {
      // Move the customer to Installation Completed stage with automation preferences
      await apiRequest('POST', '/api/pipeline/history/move', {
        customerId: pendingStageChange.customerId,
        fromStageId: pendingStageChange.customer.pipelineStageId,
        toStageId: pendingStageChange.toStageId,
        automationPreferences: preferences,
      });

      toast({
        title: 'Installation Completed',
        description: `${pendingStageChange.customer.name} has been moved to Installation Completed stage`,
      });

      // Refresh pipeline data
      queryClient.invalidateQueries({ queryKey: ['/api/pipeline-stages/data'] });
    } catch (err) {
      const error = err as Error;
      toast({
        title: 'Error',
        description: `Failed to move customer: ${error.message}`,
        variant: 'destructive',
      });
    } finally {
      actions.closeModals();
    }
  };

  return (
    <div className="-m-4 min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header with search, view mode, layout toggles, add lead */}
      <Pipeline.Header />

      {/* Main content area */}
      <div className="w-full px-4 py-4">
        {/* Simple Filters */}
        <SimplePipelineFilters
          filters={filters}
          onFiltersChange={actions.setFilters}
          customerCount={filteredCustomers.length}
          totalCustomers={state.customers?.length || 0}
        />

        {/* Global Search Results Panel */}
        {highlightTerms.length > 0 && (
          <GlobalSearchResultsPanel
            totalMatches={totalMatches}
            results={globalSearchResults}
            stageSummaries={stageSummaries}
            highlightTerms={highlightTerms}
            onSelectCustomer={actions.selectCustomerFromSearch}
            onScrollToStage={actions.scrollStageIntoView}
            onClearSearch={() => actions.setSearchQuery('')}
            focusMatchesOnly={focusMatchesOnly}
            onToggleFocusMatches={actions.toggleFocusMatchesOnly}
            stageLookup={stageById}
          />
        )}

        {/* Calendar View */}
        <div
          className={viewMode !== 'calendar' ? 'hidden' : ''}
          style={{
            position: 'relative',
            zIndex: 1, // Below header's z-50 to prevent cards overlapping sticky header
          }}
        >
          {viewMode === 'calendar' && stages && (
            <CalendarPipelineView
              customers={filteredCustomers}
              stages={stages}
              onCustomerClick={(customer) => setLocation(`/customers/${customer.id}`)}
            />
          )}
        </div>

        {/* Pipeline View */}
        <div
          className={viewMode !== 'pipeline' ? 'hidden' : ''}
          style={{
            position: 'relative',
            zIndex: 1, // Below header's z-50 to prevent cards overlapping sticky header
            contain: 'layout', // Prevent layout thrashing from stage animations
          }}
        >
          {viewMode === 'pipeline' && (
            <>
              {/* Overview stats bar */}
              <Pipeline.Overview />

              {/* Top trash drop zone */}
              <Pipeline.TrashZone variant="compact" />

              {/* Pipeline board with stages and cards */}
              <Pipeline.Board />
            </>
          )}
        </div>
      </div>

      {/* Page-level modals - must stay here for backend integrations */}
      <PipelineModals
        wonModalOpen={wonModalOpen}
        installationModalOpen={installationModalOpen}
        pendingStageChange={pendingStageChange}
        bookingPopupOpen={bookingPopupOpen}
        bookingCustomer={bookingCustomer}
        onWonConfirm={handleWonModalConfirm}
        onInstallationConfirm={handleInstallationModalConfirm}
        onCloseModals={actions.closeModals}
        onCloseBooking={actions.closeBookingPopup}
      />

      {/* Customer detail slide-out sheet */}
      <PipelineDetailSheet
        open={detailSheetOpen}
        onOpenChange={(open) => {
          if (!open) actions.closeCustomerDetail();
        }}
        customer={selectedCustomer}
      />
    </div>
  );
}

/**
 * Error state component
 */
function PipelineErrorState() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-2xl flex items-center justify-center shadow-lg mb-4 mx-auto">
          <X className="h-8 w-8 text-red-500" />
        </div>
        <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2">
          Error Loading Pipeline
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          Unable to load pipeline data. Please try again later.
        </p>
        <Button
          onClick={() => window.location.reload()}
          className="bg-primary hover:bg-primary/90"
        >
          Reload Page
        </Button>
      </div>
    </div>
  );
}

/**
 * Pipeline modals component - handles Won, Installation, and Booking modals
 * These MUST stay at page level because they trigger critical backend integrations:
 * - Won: Meta CAPI event, LOA email, Solar Journey email, Installation creation
 * - Installation: Installation complete email sequence
 * - Booking: MS Graph Calendar integration
 */
interface PipelineModalsProps {
  wonModalOpen: boolean;
  installationModalOpen: boolean;
  pendingStageChange: {
    customerId: number;
    customer: {
      id: number;
      name: string;
      email?: string | null;
      phone?: string | null;
      address?: string | null;
      postcode?: string | null;
      dealValue?: number | null;
      pipelineStageId?: number | null;
      pipelineNotes?: string | null;
      additionalData?: Record<string, unknown> | null;
    };
    toStageId: number;
    stageName: string;
  } | null;
  bookingPopupOpen: boolean;
  bookingCustomer: {
    id: number;
    name: string;
    email?: string | null;
    phone?: string | null;
    address?: string | null;
    postcode?: string | null;
    dealValue?: number | null;
    additionalData?: Record<string, unknown> | null;
  } | null;
  onWonConfirm: (preferences: {
    sendSolarJourney: boolean;
    generateLOA: boolean;
    triggerOnboarding: boolean;
    createInstallation: boolean;
    dealValue?: number;
  }) => Promise<void>;
  onInstallationConfirm: (preferences: {
    sendInstallationEmail: boolean;
  }) => Promise<void>;
  onCloseModals: () => void;
  onCloseBooking: () => void;
}

function PipelineModals({
  wonModalOpen,
  installationModalOpen,
  pendingStageChange,
  bookingPopupOpen,
  bookingCustomer,
  onWonConfirm,
  onInstallationConfirm,
  onCloseModals,
  onCloseBooking,
}: PipelineModalsProps) {
  const { toast } = useToast();

  return (
    <>
      {/* Booking Popup */}
      {bookingCustomer && (
        <PipelineBookingPopup
          open={bookingPopupOpen}
          onOpenChange={(open) => {
            if (!open) onCloseBooking();
          }}
          customer={{
            id: bookingCustomer.id,
            name: bookingCustomer.name,
            email: bookingCustomer.email || undefined,
            phone: bookingCustomer.phone || undefined,
            address: bookingCustomer.address || undefined,
            postcode: bookingCustomer.postcode || undefined,
            opensolarProjectId: (bookingCustomer.additionalData as Record<string, unknown>)?.opensolarProjectId as string | undefined,
            dealValue: bookingCustomer.dealValue || undefined,
          }}
          onBookingComplete={(date, staffName, bookingType) => {
            toast({
              title: 'Appointment Booked',
              description: `${bookingType === 'design_call' ? 'Design Call' : 'Site Visit'} with ${staffName} scheduled for ${bookingCustomer.name}`,
            });
            // Refresh the optimized pipeline query that powers the visible board.
            queryClient.invalidateQueries({ queryKey: ['/api/pipeline-stages/data'] });
            queryClient.invalidateQueries({ queryKey: ['/api/customers'] });
          }}
        />
      )}

      {/* Won Stage Modal */}
      {pendingStageChange && pendingStageChange.stageName === 'Won' && (
        <WonStageModal
          open={wonModalOpen}
          onOpenChange={(open) => {
            if (!open) onCloseModals();
          }}
          customerName={pendingStageChange.customer.name}
          currentDealValue={pendingStageChange.customer.dealValue ?? undefined}
          onConfirm={onWonConfirm}
        />
      )}

      {/* Installation Completed Modal */}
      {pendingStageChange && pendingStageChange.stageName === 'Installation Completed' && (
        <InstallationCompletedModal
          open={installationModalOpen}
          onOpenChange={(open) => {
            if (!open) onCloseModals();
          }}
          customerName={pendingStageChange.customer.name}
          onConfirm={onInstallationConfirm}
        />
      )}
    </>
  );
}
